import { dayLabel } from './format'
import type { Stage } from './types'

/**
 * Der Kalender der Reise: welcher Tag zu welcher Etappe gehört.
 *
 * Eine Etappe ist ein Tag - so ist die Planung angelegt. Die Frage, die diese
 * Datei beantwortet, ist die eine, die daran nicht von selbst mitkommt: **zählt
 * eine ausgelassene Etappe einen Tag mit?**
 *
 * Nein. Ausgelassen heisst, dass diese Fahrt nicht stattfindet - „bleibt
 * sichtbar, fällt aus allen Summen heraus“ (Stage.skip in types.ts), und
 * activeStages nimmt sie aus jeder Rechnung. Wer die Fahrt nicht macht,
 * verbringt den Tag nicht damit; die Reise wird um diesen Tag kürzer, und alles
 * Folgende rückt vor. Ein Tag, an dem man bleibt, wo man ist, ist in dieser App
 * etwas anderes: eine Etappe ohne Fahrt (Standtag, siehe isDrive in route.ts)
 * oder ein Puffertag (Stage.optional) - beide zählen mit, weil beide
 * stattfinden.
 *
 * Deshalb zählt der Reisetag die aktiven Etappen und nicht die Tagesnummer.
 * Stage.day ist die Stelle in der Liste einschliesslich der ausgelassenen
 * (renumber in trip.ts) und beantwortet damit eine andere Frage: „die
 * wievielte Etappe“, nicht „der wievielte Tag“. Solange keine Etappe
 * ausgelassen ist, sind beide Zahlen gleich - genau deshalb ist der Unterschied
 * auf der ersten Reise niemandem aufgefallen.
 *
 * Die Rechnung steht hier und nicht zweimal nebeneinander: das Datum, unter dem
 * eine importierte GPX-Datei einsortiert wird (features/diary/verteilen.ts),
 * und der Zeitraum auf dem Umschlag des gedruckten Buchs
 * (features/print/metrics.ts) sind dieselbe Aussage über dieselbe Reise. Wenn
 * sie auseinanderlaufen, ist eine von beiden falsch, und der Nutzer erfährt es
 * erst am gedruckten Buch.
 *
 * Dazu gehört auch das Lesen eines Datums, und zwar aus demselben Grund:
 * parseIsoDay steht hier, weil ein Datum, das an einer Stelle durchfällt und an
 * der nächsten angenommen wird, dieselbe Reise verschieden datiert.
 *
 * Und weil die Rechnung hier steht, steht hier auch die Beschriftung: was in
 * der App oder im Buch „Tag“ heisst, ist der Reisetag und kommt aus
 * travelDayLabel. Vorher stand dort Stage.day, also die Stelle in der Liste -
 * bei drei Etappen mit ausgelassener Mitte sagte der Umschlag „zwei Tage“ und
 * die Seiten darin „Tag 01“ und „Tag 03“. Auf Papier lässt sich das nicht
 * nachschlagen. Wo wirklich die Stelle in der Liste gemeint ist - der Nachbar
 * beim Verschieben, die Reihenfolge im Etappeneditor -, heisst sie deshalb auch
 * nicht mehr „Tag“.
 */

/**
 * Wie viele Tage die Reise dauert.
 *
 * Ein Tag je Etappe, die gefahren wird. Die Liste darf die ausgelassenen
 * enthalten oder nicht - das Ergebnis ist dasselbe.
 */
export function travelDayCount(stages: readonly Stage[]): number {
  let tage = 0
  for (const stage of stages) {
    if (!stage.skip) tage++
  }
  return tage
}

/**
 * Der wievielte Reisetag diese Etappe ist, ab eins gezählt.
 *
 * Gezählt wird die Stelle in der Reihenfolge, nicht Stage.day. Das ist nicht
 * nur wegen der ausgelassenen Etappen richtig: Tagesnummern kommen auch aus
 * Importdateien (lib/transfer.ts) und sind dort eine fremde Behauptung - eine
 * doppelte oder eine 999 ergäbe ein Datum, das mit der Reise nichts zu tun hat.
 * Die Stelle in der Liste kann nicht falsch sein.
 *
 * @returns 0 für eine ausgelassene Etappe und für eine, die nicht in der Liste
 *   steht. Sie hat keinen Reisetag, und ein erfundener wäre schlechter als
 *   keiner.
 */
export function travelDay(stages: readonly Stage[], stage: Stage): number {
  if (stage.skip) return 0
  let tag = 0
  for (const s of stages) {
    if (s.skip) continue
    tag++
    if (s.id === stage.id) return tag
  }
  return 0
}

/** Ein Kalendertag in Millisekunden. */
const TAG_MS = 86_400_000

/**
 * Das ISO-Datum eines Zeitpunkts, in UTC gelesen.
 *
 * Die eine Richtung der Umrechnung. Sie steht hier, weil die andere
 * (parseIsoDay) sich auf sie stützt.
 */
export function isoDay(zeit: number): string {
  return new Date(zeit).toISOString().slice(0, 10)
}

/**
 * Ein ISO-Datum als Zeitpunkt, UTC-Mitternacht.
 *
 * Die Form allein reicht nicht. Date.parse nimmt "2026-02-30" entgegen und
 * rollt still auf den 2. März weiter, ebenso den 29. Februar eines Jahres ohne
 * Schalttag und den 31. April; erst der 32. eines Monats fällt durch. Die
 * Prüfung ist deshalb der Rückweg: nur wenn der Zeitpunkt wieder dasselbe Datum
 * ergibt, das dastand, hat es diesen Tag gegeben. Das ist die Regel, nach der
 * parseTime in lib/gpx.ts Zeitstempel aus Aufzeichnungen liest - dort steht sie
 * seit jeher, hier hat sie gefehlt.
 *
 * Von Hand ist ein solches Datum nicht einzugeben: die Oberfläche benutzt
 * überall input type="date". Aus einer Importdatei kommt es sehr wohl - weder
 * meta.start noch das Datum eines Tagebucheintrags werden beim Lesen geprüft
 * (lib/transfer.ts), und beide laufen von dort in die Datumsrechnung.
 *
 * @returns null, wenn der Text kein Kalendertag ist. Ein stillschweigend
 *   verschobener wäre schlimmer als keiner: es fehlt dann nichts, es steht nur
 *   überall etwas anderes, als in der Datei steht.
 */
export function parseIsoDay(iso: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const zeit = Date.parse(`${iso}T00:00:00Z`)
  if (!Number.isFinite(zeit)) return null
  return isoDay(zeit) === iso ? zeit : null
}

/**
 * Zeichen für eine Etappe, die keinen Reisetag hat.
 *
 * Ein Gedankenstrich und keine leere Stelle: die Zahl steht in einem Feld, das
 * in jeder Zeile dasteht, und eine leere Zelle sähe aus, als fehle die Angabe.
 * Hier fehlt sie nicht, es gibt sie nicht.
 */
export const KEIN_REISETAG = '—'

/**
 * Der Reisetag einer Etappe, wie er dransteht: zweistellig, etwa "03".
 *
 * Zweistellig, damit die Zahlen in einer Liste untereinander fluchten und im
 * Navigationsgerät „Tag 02“ vor „Tag 10“ steht.
 *
 * Die Liste darf die ausgelassenen Etappen enthalten oder nicht - travelDay
 * überspringt sie ohnehin. Sie muss aber vollständig sein: eine auf eine
 * einzige Etappe gefilterte Liste ergäbe für jede Etappe den ersten Tag.
 *
 * @returns KEIN_REISETAG für eine ausgelassene Etappe und für eine, die nicht
 *   in der Liste steht. Eine erfundene Zahl wäre schlechter als keine - und
 *   dass eine ausgelassene Etappe keinen Tag kostet, ist genau die Auskunft,
 *   die hier hingehört.
 */
export function travelDayLabel(stages: readonly Stage[], stage: Stage): string {
  const tag = travelDay(stages, stage)
  return tag > 0 ? dayLabel(tag) : KEIN_REISETAG
}

/**
 * Das Datum des n-ten Reisetags, als ISO-Datum.
 *
 * Gerechnet wird in UTC-Mitternacht und ganzen Tagen: ein Reisetag ist ein
 * Kalendertag, und die Sommerzeit fällt zwischen Bern und Bukarest zwar an,
 * verschiebt aber keinen Kalendertag.
 *
 * @returns Leer, wenn die Reise kein brauchbares Startdatum hat oder die
 *   Tagesnummer keine ist. Ein erfundenes Datum wäre schlimmer als keines.
 */
export function travelDayDate(startIso: string, tag: number): string {
  const start = parseIsoDay(startIso)
  if (start === null) return ''
  if (!Number.isFinite(tag) || tag < 1) return ''
  return isoDay(start + (Math.floor(tag) - 1) * TAG_MS)
}
